import { useContext, useEffect, useState } from 'react';
import { BraintreeCreditCardForm } from './BraintreeCreditCardForm';
import { ApplePaySession, Client, client } from 'braintree-web';
import { BraintreeApplePay } from './BraintreeApplePayForm';
import { styled } from '@mui/material';
import { APPLE_PAY_VERSION } from '@/constants/applePay';
import { BindContext } from '@/contexts/bind/bind-context';

interface BraintreePaymentProps {
  client_token: string;
  exclude_applepay: boolean;
  legal: string;
  amount: string;
}

function isApplePaySupported() {
  if (window.location.protocol === 'https' && window.ApplePaySession) {
    const applePaySession = ApplePaySession;
    if (applePaySession.supportsVersion(APPLE_PAY_VERSION) && applePaySession.canMakePayments()) {
      return true;
    }
  }

  return false;
}

export function BraintreePayment({ client_token, exclude_applepay, legal, amount }: BraintreePaymentProps) {
  const [clientInstance, setClientInstance] = useState<Client | null>(null);

  const showApplePay = isApplePaySupported() && !exclude_applepay;

  // eslint-disable-next-line @typescript-eslint/unbound-method
  const { submitBind } = useContext(BindContext);

  // Initialize Braintree client
  useEffect(() => {
    async function createClient() {
      const instance = await client.create({
        authorization: client_token,
      });
      void setClientInstance(instance);
    }
    void createClient();
  }, [client_token]);

  return (
    <>
      {clientInstance && showApplePay && (
        <>
          <BraintreeApplePay
            onSuccess={submitBind}
            clientInstance={clientInstance}
            legal={legal}
            amount={amount}
          />
          <StyledDivider />
        </>
      )}

      {clientInstance && (
        <BraintreeCreditCardForm
          onSuccess={submitBind}
          clientInstance={clientInstance}
          legal={legal}
          amount={amount}
        />
      )}
    </>
  );
}

const StyledDivider = styled('hr')`
  margin: 1rem 0;
  position: relative;
  display: block;
  width: 100%;
  border-color: ${({ theme }) => theme.palette.divider};
  border-style: solid;
  border-width: 1px 0 0 0;
  overflow: visible;

  &::after {
    content: 'or';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    color: ${({ theme }) => theme.palette.text.secondary};
    background-color: ${({ theme }) => theme.palette.background.paper};
    font-size: ${({ theme }) => theme.typography.body1.fontSize};
    width: 4.5rem;
    text-align: center;
  }
`;
